import { api, type PushPlatform } from "../sync/api";
import { isAndroid, isIOS, swRegistration } from "./device";

/**
 * Web Push, the phone's half (ADR-044; the server is ADR-045's).
 *
 * A PWA cannot schedule a notification of its own once it is closed — Chrome ended Notification
 * Triggers, and the service-worker timers in `reminders.ts` die with the worker. So water
 * reminders are pushed by Kaya's server. This is where a device subscribes and unsubscribes, and
 * where it tells the server the app is on screen so no push is wasted on a page already open.
 *
 * The VAPID public key comes from the server (`/push/key`), never from the build, so the key a
 * browser subscribes with is always the key the server signs with.
 */

/** This device's subscription endpoint, kept so the presence ping and `reminders.ts` need not
 *  wait on the worker to ask whether this device is subscribed. */
const ENDPOINT_KEY = "kaya-push-endpoint";

/** Whether this browser can receive a push at all. An iPhone's Safari tab cannot; the same
 *  iPhone can once Kaya is on the Home Screen (iOS 16.4+). Feature detection, not the user agent. */
export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    typeof Notification !== "undefined"
  );
}

export function pushPlatform(): PushPlatform {
  if (isIOS()) return "ios";
  if (isAndroid()) return "android";
  return typeof window !== "undefined" ? "desktop" : "other";
}

/** The endpoint this device last subscribed with, or null. */
export function pushEndpoint(): string | null {
  try {
    return localStorage.getItem(ENDPOINT_KEY);
  } catch {
    return null;
  }
}

function rememberEndpoint(e: string | null): void {
  try {
    if (e) localStorage.setItem(ENDPOINT_KEY, e);
    else localStorage.removeItem(ENDPOINT_KEY);
  } catch {
    /* private mode — the presence ping simply does not run */
  }
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = "=".repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function sameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a || a.byteLength !== b.length) return false;
  const x = new Uint8Array(a);
  return x.every((v, i) => v === b[i]);
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  const r = swRegistration();
  if (r) return r;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

/** The server's public key, or null when push is not set up there (or it cannot be reached). */
export async function loadPushKey(): Promise<string | null> {
  try {
    return (await api.pushKey()).key;
  } catch {
    return null;
  }
}

export type SubscribeResult = "on" | "denied" | "unsupported" | "failed";

/**
 * Subscribes this device with `key` and hands the subscription to the server.
 *
 * Must run from a tap, and the permission request must be its first wait: iOS grants
 * notification permission to a gesture only, which is why the key is fetched before the tap
 * rather than here. A subscription made with an older key is replaced, not reused — the push
 * services refuse a key the subscription was not made with.
 */
export async function subscribePush(key: string): Promise<SubscribeResult> {
  if (!pushSupported()) return "unsupported";
  try {
    if (Notification.permission !== "granted" && (await Notification.requestPermission()) !== "granted") return "denied";
    const reg = await registration();
    if (!reg) return "unsupported";
    const want = keyBytes(key);
    let sub = await reg.pushManager.getSubscription();
    if (sub && !sameKey(sub.options.applicationServerKey, want)) {
      await sub.unsubscribe();
      sub = null;
    }
    sub = sub ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: want }));
    await api.pushSubscribe(sub.toJSON(), pushPlatform());
    rememberEndpoint(sub.endpoint);
    return "on";
  } catch {
    return "failed";
  }
}

/** Takes this device off the server's list and ends the browser's subscription. Best effort: a
 *  server that cannot be reached drops the row itself at the next send, on the 410. */
export async function unsubscribePush(): Promise<void> {
  rememberEndpoint(null);
  if (!pushSupported()) return;
  try {
    const reg = await registration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe();
  } catch {
    /* nothing to undo */
  }
}

/** What the settings line says about this device. */
export type PushHere =
  /** this browser cannot receive a push (a Safari tab, an old browser) */
  | { kind: "unsupported" }
  /** the server has no VAPID keys yet, or could not be reached */
  | { kind: "unavailable" }
  /** notifications are blocked for Kaya in the browser's settings */
  | { kind: "blocked" }
  /** not subscribed here — or the server dropped the subscription since */
  | { kind: "none" }
  | { kind: "on"; lastOkAt: number | null };

/** Asks the browser and the server together: either alone can be wrong about the other. */
export async function pushHere(): Promise<PushHere> {
  if (!pushSupported()) return { kind: "unsupported" };
  if (Notification.permission === "denied") return { kind: "blocked" };
  try {
    const reg = await registration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) {
      rememberEndpoint(null);
      const key = await loadPushKey();
      return key ? { kind: "none" } : { kind: "unavailable" };
    }
    const st = await api.pushStatus(sub.endpoint);
    if (!st.configured) return { kind: "unavailable" };
    rememberEndpoint(st.subscribed ? sub.endpoint : null);
    return st.subscribed ? { kind: "on", lastOkAt: st.lastOkAt } : { kind: "none" };
  } catch {
    return { kind: "unavailable" };
  }
}

/**
 * "Kaya is on screen here." The server sends this device nothing for a few minutes after. Called
 * while the page is visible; silent on every failure — a missed ping costs one reminder shown to
 * someone already looking at the app, which the worker then holds back anyway (invariant 17).
 */
export function pushSeen(): void {
  const e = pushEndpoint();
  if (!e) return;
  void api.pushSeen(e).catch(() => undefined);
}
